import { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/common/Input';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Select, CountrySelect } from '@/components/form';
import { Badge } from '@/components/data-display/Badge';
import type { Person, PersonRights } from '../types';
import { ScreeningBadge } from '../ScreeningBadge';
import type {
  CreatePersonPayload,
  UpdatePersonPayload,
  UpdatePersonRightsPayload,
  GrantPortalAccessStatus,
  ResendInvitationStatus,
  PersonLookupFields,
  PersonLookupResult,
} from '../useClientDetail';
import { EmailInput, PhoneInput, isPhoneValid } from '../inputs';
import { maxAdultBirthDate } from '../format';
import { useLookup } from '@/hooks/useLookups';
import { validateEmail } from '../validation';

interface PersonsTabProps {
  clientId: string;
  persons: Person[];
  loading: boolean;
  onLoad: () => Promise<void>;
  onCreate: (payload: CreatePersonPayload) => Promise<void>;
  onLookup: (email: string) => Promise<PersonLookupResult>;
  onUpdate: (personId: string, payload: UpdatePersonPayload) => Promise<void>;
  onDelete: (personId: string) => Promise<void>;
  onGrantPortalAccess: (personId: string) => Promise<GrantPortalAccessStatus>;
  onResendInvitation: (personId: string) => Promise<ResendInvitationStatus>;
  onGetRights: (personId: string) => Promise<PersonRights>;
  onUpdateRights: (personId: string, payload: UpdatePersonRightsPayload) => Promise<void>;
}

// All text fields in the form are stored as strings so the UI can stay
// controlled; empty strings are converted to null before submit so that
// class-validator `@IsOptional` on the backend accepts them.
export interface PersonForm {
  roleId: string;
  firstName: string;
  lastName: string;
  email: string;
  gender: string;
  dateOfBirth: string;
  nationalityCountryCode: string;
  residenceCountryCode: string;
  telephone: string;
  ownershipPercent: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  addressCountryCode: string;
  isPep: boolean;
  pepName: string;
  pepRelationship: string;
  usResident: boolean;
  usTin: string;
  residenceTin: string;
  // Login + initial portal permissions. Only meaningful at create time;
  // existing person edits manage these via the rights modal and the
  // separate canLogin PATCH endpoint, so the create-mode-only branch
  // below conditionally renders these inputs.
  canLogin: boolean;
  canViewTransactions: boolean;
  canCreateTransfer: boolean;
  canApprovePayment: boolean;
  canCreateFx: boolean;
  canManageBeneficiary: boolean;
}

export const EMPTY_FORM: PersonForm = {
  roleId: '',
  firstName: '',
  lastName: '',
  email: '',
  gender: '',
  dateOfBirth: '',
  nationalityCountryCode: '',
  residenceCountryCode: '',
  telephone: '',
  ownershipPercent: '',
  addressLine1: '',
  addressLine2: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  addressCountryCode: '',
  isPep: false,
  pepName: '',
  pepRelationship: '',
  usResident: false,
  usTin: '',
  residenceTin: '',
  canLogin: false,
  canViewTransactions: false,
  canCreateTransfer: false,
  canApprovePayment: false,
  canCreateFx: false,
  canManageBeneficiary: false,
};

type FormErrors = Partial<Record<keyof PersonForm, string>>;

const trimOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Prefill the form from an existing person found by "Lookup existing".
 * Role, ownership and portal access are company-specific: the role is
 * cleared so the operator must pick it for this company, ownership is left
 * as typed, and "Can log in" plus every right are reset to off — access to
 * this company is only granted when the operator ticks it explicitly.
 */
export function applyLookupToForm(form: PersonForm, person: PersonLookupFields): PersonForm {
  return {
    ...form,
    roleId: '',
    firstName: person.first_name,
    lastName: person.last_name,
    email: person.email,
    gender: person.gender ?? '',
    dateOfBirth: person.date_of_birth ? person.date_of_birth.slice(0, 10) : '',
    nationalityCountryCode: person.nationality_country_code ?? '',
    residenceCountryCode: person.residence_country_code ?? '',
    telephone: person.telephone ?? '',
    addressLine1: person.address_line1 ?? '',
    addressLine2: person.address_line2 ?? '',
    addressCity: person.address_city ?? '',
    addressState: person.address_state ?? '',
    addressZip: person.address_zip ?? '',
    addressCountryCode: person.address_country_code ?? '',
    isPep: person.is_pep,
    pepName: person.pep_name ?? '',
    pepRelationship: person.pep_relationship ?? '',
    usResident: person.us_resident,
    usTin: person.us_tin ?? '',
    residenceTin: person.residence_tin ?? '',
    canLogin: false,
    canViewTransactions: false,
    canCreateTransfer: false,
    canApprovePayment: false,
    canCreateFx: false,
    canManageBeneficiary: false,
  };
}

const normaliseEmail = (value: string): string => value.trim().toLowerCase();

/**
 * The form stays linked to the person found by the lookup only while the
 * email is unchanged; editing it turns the submit back into a new person.
 */
export function isLinkedToLookup(form: PersonForm, linkedEmail: string | null): boolean {
  return linkedEmail !== null && normaliseEmail(form.email) === linkedEmail;
}

type LookupState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'found'; email: string }
  | { kind: 'already_associated' };

export function buildPayload(
  form: PersonForm,
  isEdit: boolean,
  associateExisting = false,
): CreatePersonPayload {
  const ownership = form.ownershipPercent.trim().length > 0 ? Number(form.ownershipPercent) : null;
  const base: CreatePersonPayload = {
    roleId: Number(form.roleId),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim(),
    gender: trimOrNull(form.gender),
    dateOfBirth: trimOrNull(form.dateOfBirth),
    nationalityCountryCode: trimOrNull(form.nationalityCountryCode),
    residenceCountryCode: trimOrNull(form.residenceCountryCode),
    telephone: trimOrNull(form.telephone),
    ownershipPercent: ownership === null || Number.isNaN(ownership) ? null : ownership,
    addressLine1: trimOrNull(form.addressLine1),
    addressLine2: trimOrNull(form.addressLine2),
    addressCity: trimOrNull(form.addressCity),
    addressState: trimOrNull(form.addressState),
    addressZip: trimOrNull(form.addressZip),
    addressCountryCode: trimOrNull(form.addressCountryCode),
    isPep: form.isPep,
    pepName: form.isPep ? trimOrNull(form.pepName) : null,
    pepRelationship: form.isPep ? trimOrNull(form.pepRelationship) : null,
    usResident: form.usResident,
    usTin: form.usResident ? trimOrNull(form.usTin) : null,
    residenceTin: trimOrNull(form.residenceTin),
  };
  // The canLogin + permissions block is create-only. In edit mode, login
  // status is managed by the existing access generation flow and rights
  // are managed by the rights modal — sending them here would create two
  // sources of truth.
  if (!isEdit) {
    if (associateExisting) base.associateExisting = true;
    base.canLogin = form.canLogin;
    if (form.canLogin) {
      base.canViewTransactions = form.canViewTransactions;
      base.canCreateTransfer = form.canCreateTransfer;
      base.canApprovePayment = form.canApprovePayment;
      base.canCreateFx = form.canCreateFx;
      base.canManageBeneficiary = form.canManageBeneficiary;
    }
  }
  return base;
}

export function PersonsTab({
  clientId: _clientId,
  persons,
  loading,
  onLoad,
  onCreate,
  onLookup,
  onUpdate,
  onDelete,
  onGrantPortalAccess,
  onResendInvitation,
  onGetRights,
  onUpdateRights,
}: PersonsTabProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [form, setForm] = useState<PersonForm>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [lookup, setLookup] = useState<LookupState>({ kind: 'idle' });
  // "No existing person found" is reported in a dialog over the form.
  const [showLookupNotFound, setShowLookupNotFound] = useState(false);

  // Email the lookup matched; null when the form describes a new person.
  const linkedEmail = lookup.kind === 'found' ? lookup.email : null;

  // Identity of the latest lookup request. Bumped when a lookup starts and
  // whenever its answer would no longer belong to the form (email edited,
  // modal closed, another form opened), so late responses are dropped.
  const lookupRequestId = useRef(0);
  const invalidateLookup = useCallback(() => {
    lookupRequestId.current += 1;
  }, []);

  const { items: roleOptions, loading: rolesLoading } = useLookup('person-roles');

  // Delete confirmation
  const [deletingPerson, setDeletingPerson] = useState<Person | null>(null);

  // Rights modal
  const [rightsModal, setRightsModal] = useState<{ person: Person; rights: PersonRights } | null>(
    null,
  );
  const [rightsForm, setRightsForm] = useState<UpdatePersonRightsPayload>({
    can_view_transactions: false,
    can_create_transfer: false,
    can_approve_payment: false,
    can_create_fx: false,
    can_manage_beneficiary: false,
  });

  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  const openCreate = useCallback(() => {
    invalidateLookup();
    setEditingPerson(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setLookup({ kind: 'idle' });
    setShowModal(true);
  }, [invalidateLookup]);

  const closeModal = useCallback(() => {
    invalidateLookup();
    setLookup({ kind: 'idle' });
    setShowLookupNotFound(false);
    setShowModal(false);
  }, [invalidateLookup]);

  const handleLookup = async () => {
    const email = form.email.trim();
    if (!email) {
      setFormErrors((prev) => ({ ...prev, email: 'Enter an email to look up' }));
      return;
    }
    if (!validateEmail(email)) {
      setFormErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
      return;
    }
    const requestId = ++lookupRequestId.current;
    const isCurrent = () => requestId === lookupRequestId.current;
    setLookup({ kind: 'loading' });
    try {
      const result = await onLookup(email);
      if (!isCurrent()) return;
      if (result.status === 'found') {
        setForm((prev) => applyLookupToForm(prev, result.person));
        setFormErrors({});
        setLookup({ kind: 'found', email: normaliseEmail(result.person.email) });
      } else if (result.status === 'not_found') {
        setLookup({ kind: 'idle' });
        setShowLookupNotFound(true);
      } else {
        setLookup({ kind: result.status });
      }
    } catch {
      // Error already toasted by hook
      if (isCurrent()) setLookup({ kind: 'idle' });
    }
  };

  const openEdit = useCallback(
    (person: Person) => {
      invalidateLookup();
      setEditingPerson(person);
      setForm({
        roleId: person.role_id != null ? String(person.role_id) : '',
        firstName: person.first_name,
        lastName: person.last_name,
        email: person.email,
        gender: person.gender ?? '',
        dateOfBirth: person.date_of_birth ? String(person.date_of_birth).slice(0, 10) : '',
        nationalityCountryCode: person.nationality_country_code ?? '',
        residenceCountryCode: person.residence_country_code ?? '',
        telephone: person.telephone ?? person.phone ?? '',
        ownershipPercent: person.ownership_percent ?? '',
        addressLine1: person.address_line1 ?? '',
        addressLine2: person.address_line2 ?? '',
        addressCity: person.address_city ?? '',
        addressState: person.address_state ?? '',
        addressZip: person.address_zip ?? '',
        addressCountryCode: person.address_country_code ?? '',
        isPep: person.is_pep ?? false,
        pepName: person.pep_name ?? '',
        pepRelationship: person.pep_relationship ?? '',
        usResident: person.us_resident ?? false,
        usTin: person.us_tin ?? '',
        residenceTin: person.residence_tin ?? '',
        // create-only flags — never surfaced in edit mode
        canLogin: false,
        canViewTransactions: false,
        canCreateTransfer: false,
        canApprovePayment: false,
        canCreateFx: false,
        canManageBeneficiary: false,
      });
      setFormErrors({});
      setLookup({ kind: 'idle' });
      setShowModal(true);
    },
    [invalidateLookup],
  );

  const validate = (): boolean => {
    const errors: FormErrors = {};
    if (!form.roleId) errors.roleId = 'Role is required';
    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!validateEmail(form.email)) errors.email = 'Invalid email format';
    if (!isPhoneValid(form.telephone)) {
      errors.telephone = 'Invalid phone number — include the country code (e.g. +33…)';
    }
    if (form.ownershipPercent.trim().length > 0) {
      const n = Number(form.ownershipPercent);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        errors.ownershipPercent = 'Must be between 0 and 100';
      }
    }
    if (form.isPep && !form.pepName.trim()) {
      errors.pepName = 'PEP name is required when PEP is checked';
    }
    if (form.usResident && !form.usTin.trim()) {
      errors.usTin = 'US TIN is required for US residents';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = buildPayload(
        form,
        editingPerson !== null,
        isLinkedToLookup(form, linkedEmail),
      );
      if (editingPerson) {
        await onUpdate(editingPerson.id, payload);
      } else {
        await onCreate(payload);
      }
      closeModal();
    } catch {
      // Error already toasted by hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPerson) return;
    try {
      await onDelete(deletingPerson.id);
    } catch {
      // Error already toasted
    }
    setDeletingPerson(null);
  };

  const handleGrantPortalAccess = async (person: Person) => {
    try {
      await onGrantPortalAccess(person.id);
    } catch {
      // Error already toasted
    }
  };

  const handleResendInvitation = async (person: Person) => {
    try {
      await onResendInvitation(person.id);
    } catch {
      // Error already toasted
    }
  };

  const openRights = async (person: Person) => {
    try {
      const rights = await onGetRights(person.id);
      setRightsForm({
        can_view_transactions: rights.can_view_transactions,
        can_create_transfer: rights.can_create_transfer,
        can_approve_payment: rights.can_approve_payment,
        can_create_fx: rights.can_create_fx,
        can_manage_beneficiary: rights.can_manage_beneficiary,
      });
      setRightsModal({ person, rights });
    } catch {
      // Error already toasted
    }
  };

  const saveRights = async () => {
    if (!rightsModal) return;
    try {
      await onUpdateRights(rightsModal.person.id, rightsForm);
      setRightsModal(null);
    } catch {
      // Error already toasted
    }
  };

  const updateField = <K extends keyof PersonForm>(field: K, value: PersonForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'email') {
      // A pending lookup was for the previous email; a finished one keeps
      // its result so the "Email changed" warning can show.
      invalidateLookup();
      setLookup((prev) => (prev.kind === 'loading' ? { kind: 'idle' } : prev));
    }
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (loading) {
    return <LoadingSpinner size="md" text="Loading persons..." />;
  }

  const roleSelectOptions = roleOptions.map((r) => ({
    value: String(r.id),
    label: r.label,
  }));

  const lookupMessage: { text: string; tone: 'info' | 'warning' } | null = (() => {
    switch (lookup.kind) {
      case 'found':
        return isLinkedToLookup(form, linkedEmail)
          ? {
              text: 'Existing person found — details filled in. Select their role for this company.',
              tone: 'info',
            }
          : { text: 'Email changed — this will be added as a new person.', tone: 'warning' };
      case 'already_associated':
        return { text: 'This person is already associated with this company', tone: 'warning' };
      default:
        return null;
    }
  })();

  return (
    <div>
      <div className="mb-4">
        <Button size="sm" onClick={openCreate}>
          + Add Person
        </Button>
      </div>

      {persons.length === 0 ? (
        <div className="rounded-xl border border-[#eee] bg-white p-6 text-center text-[#999]">
          No individuals registered
        </div>
      ) : (
        <div className="space-y-3">
          {persons.map((person) => (
            <div key={person.id} className="rounded-xl border border-[#eee] bg-white p-5">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#6B0F1A] to-[#3D0008] text-[14px] font-bold text-white">
                    {person.first_name[0]}
                    {person.last_name[0]}
                  </div>
                  <div>
                    <div className="text-[14px] font-bold text-[#1a1a1a]">
                      {person.first_name} {person.last_name}
                    </div>
                    <div className="mt-1 flex gap-1.5">
                      {person.has_portal_access && <Badge variant="success">Portal Access</Badge>}
                      <Badge variant={person.is_active ? 'success' : 'default'}>
                        {person.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <ScreeningBadge screeningStatusId={person.screening_status_id} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {!person.has_portal_access ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-[#2e7d32] text-white border-[#2e7d32] hover:bg-[#1b5e20]"
                      onClick={() => void handleGrantPortalAccess(person)}
                    >
                      Grant Portal Access
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void handleResendInvitation(person)}
                    >
                      Resend Invitation
                    </Button>
                  )}
                  {person.has_portal_access && (
                    <Button variant="outline" size="sm" onClick={() => void openRights(person)}>
                      Permissions
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openEdit(person)}>
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-[#c62828]"
                    onClick={() => setDeletingPerson(person)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">Email</div>
                  <div className="text-[13px] text-[#1a1a1a]">{person.email}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">Phone</div>
                  <div className="text-[13px] text-[#1a1a1a]">
                    {person.telephone ?? person.phone ?? '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                    Date of Birth
                  </div>
                  <div className="text-[13px] text-[#1a1a1a]">
                    {person.date_of_birth
                      ? (() => {
                          const [y, m, d] = person.date_of_birth.split('-').map(Number);
                          return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          });
                        })()
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                    Ownership
                  </div>
                  <div className="text-[13px] text-[#1a1a1a]">
                    {person.ownership_percent != null && person.ownership_percent !== ''
                      ? `${person.ownership_percent}%`
                      : '—'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Person Modal */}
      <Modal
        isOpen={showModal}
        title={editingPerson ? 'Edit Person' : 'Add Person'}
        onClose={closeModal}
        className="max-w-2xl"
        footer={
          <>
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
          {/* Identity */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              Identity
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">Role *</label>
                <Select
                  value={form.roleId}
                  onChange={(v) => updateField('roleId', v)}
                  options={roleSelectOptions}
                  placeholder={rolesLoading ? 'Loading roles…' : 'Select role…'}
                  error={formErrors.roleId}
                  disabled={rolesLoading}
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  First Name *
                </label>
                <Input
                  value={form.firstName}
                  onChange={(v) => updateField('firstName', v)}
                  error={formErrors.firstName}
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Last Name *
                </label>
                <Input
                  value={form.lastName}
                  onChange={(v) => updateField('lastName', v)}
                  error={formErrors.lastName}
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">Gender</label>
                <Select
                  value={form.gender}
                  onChange={(v) => updateField('gender', v)}
                  options={[
                    { value: 'M', label: 'Male' },
                    { value: 'F', label: 'Female' },
                  ]}
                  placeholder="—"
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={form.dateOfBirth}
                  onChange={(e) => updateField('dateOfBirth', e.target.value)}
                  max={maxAdultBirthDate()}
                  className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Ownership %
                </label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={form.ownershipPercent}
                  onChange={(v) => {
                    if (v === '') {
                      updateField('ownershipPercent', '');
                      return;
                    }
                    const n = Number(v);
                    if (!Number.isFinite(n)) {
                      updateField('ownershipPercent', '');
                      return;
                    }
                    const clamped = Math.min(100, Math.max(0, n));
                    updateField('ownershipPercent', String(clamped));
                  }}
                  error={formErrors.ownershipPercent}
                  placeholder="0 – 100"
                />
              </div>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              Contact
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {!editingPerson && (
                <div className="col-span-2 flex items-center gap-2.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleLookup()}
                    disabled={lookup.kind === 'loading'}
                  >
                    {lookup.kind === 'loading' ? 'Looking up…' : 'Lookup existing'}
                  </Button>
                  {lookupMessage && (
                    <span
                      role="status"
                      data-testid="person-lookup-status"
                      className={`text-[12px] ${lookupMessage.tone === 'warning' ? 'text-[#c62828]' : 'text-[#666]'}`}
                    >
                      {lookupMessage.text}
                    </span>
                  )}
                </div>
              )}
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">Email *</label>
                <EmailInput
                  value={form.email}
                  onChange={(v) => updateField('email', v)}
                  error={formErrors.email}
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Telephone
                </label>
                <PhoneInput
                  value={form.telephone}
                  onChange={(v) => updateField('telephone', v)}
                  error={formErrors.telephone}
                  placeholder="+352…"
                />
              </div>
            </div>
          </section>

          {/* Residency */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              Residency
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Nationality
                </label>
                <CountrySelect
                  value={form.nationalityCountryCode}
                  onChange={(v) => updateField('nationalityCountryCode', v)}
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Residence Country
                </label>
                <CountrySelect
                  value={form.residenceCountryCode}
                  onChange={(v) => updateField('residenceCountryCode', v)}
                />
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Residence TIN
                </label>
                <Input value={form.residenceTin} onChange={(v) => updateField('residenceTin', v)} />
              </div>
            </div>
          </section>

          {/* Address */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              Address
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Address Line 1
                </label>
                <Input value={form.addressLine1} onChange={(v) => updateField('addressLine1', v)} />
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Address Line 2
                </label>
                <Input value={form.addressLine2} onChange={(v) => updateField('addressLine2', v)} />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">City</label>
                <Input value={form.addressCity} onChange={(v) => updateField('addressCity', v)} />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  State / Region
                </label>
                <Input value={form.addressState} onChange={(v) => updateField('addressState', v)} />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Postal Code
                </label>
                <Input value={form.addressZip} onChange={(v) => updateField('addressZip', v)} />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">Country</label>
                <CountrySelect
                  value={form.addressCountryCode}
                  onChange={(v) => updateField('addressCountryCode', v)}
                />
              </div>
            </div>
          </section>

          {/* PEP */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              Politically Exposed Person
            </h3>
            <label className="mb-3 flex items-center gap-2.5 text-[13px] cursor-pointer">
              <input
                type="checkbox"
                checked={form.isPep}
                onChange={(e) => updateField('isPep', e.target.checked)}
                className="accent-[#6B0F1A]"
              />
              This person is a politically exposed person (PEP)
            </label>
            {form.isPep && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                    PEP Name *
                  </label>
                  <Input
                    value={form.pepName}
                    onChange={(v) => updateField('pepName', v)}
                    error={formErrors.pepName}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                    PEP Relationship
                  </label>
                  <Input
                    value={form.pepRelationship}
                    onChange={(v) => updateField('pepRelationship', v)}
                  />
                </div>
              </div>
            )}
          </section>

          {/* US tax */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              US Tax
            </h3>
            <label className="mb-3 flex items-center gap-2.5 text-[13px] cursor-pointer">
              <input
                type="checkbox"
                checked={form.usResident}
                onChange={(e) => updateField('usResident', e.target.checked)}
                className="accent-[#6B0F1A]"
              />
              This person is a US resident / US person for tax purposes
            </label>
            {form.usResident && (
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">US TIN *</label>
                <Input
                  value={form.usTin}
                  onChange={(v) => updateField('usTin', v)}
                  error={formErrors.usTin}
                />
              </div>
            )}
          </section>

          {/* Portal access — create-only. Editing canLogin / permissions on
              an existing person is handled elsewhere (Generate Access
              button + the rights modal) so that the create form and the
              ongoing-management surfaces remain independent sources of
              truth. */}
          {!editingPerson && (
            <section>
              <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
                Portal Access
              </h3>
              <label className="flex items-start gap-3 rounded-md bg-[#f7f7f7] p-3 text-sm text-[#333]">
                <input
                  type="checkbox"
                  checked={form.canLogin}
                  onChange={(e) => updateField('canLogin', e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#c0c0c0] text-[#6B0F1A] focus:ring-[#6B0F1A]"
                />
                <span>
                  <span className="font-medium">Can log in to the Montelux webapp</span>
                  <span className="block text-xs text-[#777]">
                    Sends an invitation email after the person is added.
                  </span>
                </span>
              </label>
              {form.canLogin && (
                <div className="mt-3 space-y-2 rounded-md border border-[#e0e0e0] bg-white p-3">
                  <div className="text-xs font-medium text-[#555]">
                    Initial portal permissions
                    <span className="block text-[11px] font-normal text-[#999]">
                      Pick the rights granted on first login. They can be edited later from this
                      tab.
                    </span>
                  </div>
                  {(
                    [
                      ['canViewTransactions', 'View transactions and balances'],
                      ['canCreateTransfer', 'Prepare payments'],
                      ['canApprovePayment', 'Approve payments'],
                      ['canCreateFx', 'Run FX conversions'],
                      ['canManageBeneficiary', 'Manage beneficiaries'],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-sm text-[#333]">
                      <input
                        type="checkbox"
                        checked={form[key]}
                        onChange={(e) => updateField(key, e.target.checked)}
                        className="h-4 w-4 rounded border-[#c0c0c0] text-[#6B0F1A] focus:ring-[#6B0F1A]"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </Modal>

      {/* Lookup: no match — the form stays as typed, continue as a new person */}
      <Modal
        isOpen={showLookupNotFound}
        title="Lookup existing"
        onClose={() => setShowLookupNotFound(false)}
        footer={<Button onClick={() => setShowLookupNotFound(false)}>OK</Button>}
      >
        <p className="text-sm text-[#666]">
          No existing person found for this email. You can continue adding them as a new person.
        </p>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deletingPerson !== null}
        title="Delete Person"
        message={
          deletingPerson
            ? `Are you sure you want to delete ${deletingPerson.first_name} ${deletingPerson.last_name}?`
            : ''
        }
        confirmText="Delete"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeletingPerson(null)}
        isDangerous
      />

      {/* Rights Modal */}
      <Modal
        isOpen={rightsModal !== null}
        title={
          rightsModal
            ? `Permissions — ${rightsModal.person.first_name} ${rightsModal.person.last_name}`
            : 'Permissions'
        }
        onClose={() => setRightsModal(null)}
        footer={
          <>
            <Button variant="outline" onClick={() => setRightsModal(null)}>
              Cancel
            </Button>
            <Button onClick={() => void saveRights()}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          {(
            [
              ['can_view_transactions', 'Can View Transactions'],
              // Renommé : ce droit prépare un paiement, il ne l'envoie plus
              // seul dès qu'un approbateur existe chez le client (#62).
              ['can_create_transfer', 'Can Prepare Payment'],
              ['can_approve_payment', 'Can Approve Payment'],
              ['can_create_fx', 'Can Create FX'],
              ['can_manage_beneficiary', 'Can Manage Beneficiaries'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2.5 cursor-pointer text-[13px]">
              <input
                type="checkbox"
                checked={rightsForm[key]}
                onChange={(e) => setRightsForm((prev) => ({ ...prev, [key]: e.target.checked }))}
                className="accent-[#6B0F1A]"
              />
              {label}
            </label>
          ))}
        </div>
      </Modal>
    </div>
  );
}
